import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrder, newVariantId } from '../context/OrderContext';
import { api } from '../services/api';
import { Header } from '../layout';
import { useAuth } from '../hooks/useAuth';
import Swal from 'sweetalert2';
import { formatNumber } from '../utils/formatters';

const OrderSummary = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useOrder();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get order type from context ('ordine' or 'offerta')
  const orderType = state.orderType || 'ordine';
  const [articleMetadata, setArticleMetadata] = useState({}); // Stores description, unit of measure, and price
  const [discountPercentage, setDiscountPercentage] = useState(0); // Sconto Tipico (first discount)
  const [extraDiscountPercentage, setExtraDiscountPercentage] = useState(0); // Sconto Extra (second discount, applied after first)
  const [showDiscountModal, setShowDiscountModal] = useState(null); // 'tipico' or 'extra' or null

  // Prefill discount percentage from customer's DiscountFormula when customer is selected
  useEffect(() => {
    if (state.customer && state.customer.DiscountFormula !== undefined) {
      const customerDiscount = parseFloat(state.customer.DiscountFormula) || 0;
      setDiscountPercentage(customerDiscount);
    }
  }, [state.customer]);

  const getSupplyTypeLabel = (type) => {
    switch (type) {
      case 'barre':
        return 'Fornitura a Barre';
      case 'misura':
        return 'Fornitura a Misura';
      case 'progettazione':
        return 'Fornitura con Progettazione';
      case 'ricambi':
        return 'Ricambio';
      default:
        return '';
    }
  };

  const getDrainageTypeLabel = (type) => {
    switch (type) {
      case 'foro-int-est':
        return 'Foro Interno-Esterno';
      case 'foro-int':
        return 'Foro Interno';
      case 'foro-est':
        return 'Foro Esterno';
      case 'fresatura':
        return 'Fresatura';
      case 'fresatura-tappo':
        return 'Fresatura con Tappo di Finitura';
      default:
        return '';
    }
  };

  // Canonical sort order: KIT profilo → Lavorazioni (LAV) → Tappi → Spessori → altro.
  const getItemSortRank = (item) => {
    if (item?.isDrainageArticle) return 1;
    const code = item?.productCode || '';
    if (typeof code === 'string' && code.startsWith('LAV')) return 1;
    if (item?.accessoryType === 'cap') return 2;
    if (item?.accessoryType === 'spacer') return 3;
    if (item?.accessoryType) return 4; // other accessory types
    return 0; // profile/kit and anything else considered main
  };

  // Group cart items by their source/section
  const groupItemsBySection = (items) => {
    const groups = {};

    items.forEach((item, index) => {
      let sectionKey = 'other';
      let sectionTitle = 'Altri Prodotti';

      // Determine section based on item properties
      if (item.isAccessoryItem) {
        sectionKey = `accessory_${item.accessoryType}`;
        sectionTitle = `Accessori - ${item.accessoryType?.charAt(0).toUpperCase() + item.accessoryType?.slice(1)}`;
      } else if (item.profileSessionId) {
        // Items with the same profileSessionId belong to the same profile kit
        sectionKey = `profile_kit_${item.profileSessionId}`;
        sectionTitle = 'Kit Profilo NINFA';
      } else if (item.isDrainageArticle) {
        // Standalone drainage articles (not part of profile process)
        sectionKey = 'drainage';
        sectionTitle = 'Articoli Scarico';
      } else if (item.category) {
        sectionKey = item.category;
        sectionTitle = item.category.charAt(0).toUpperCase() + item.category.slice(1);
      }

      if (!groups[sectionKey]) {
        groups[sectionKey] = {
          title: sectionTitle,
          items: []
        };
      }

      // Gli articoli possono arrivare gia' indicizzati (blocchi variante): in quel caso si mantiene l'indice reale del carrello
      groups[sectionKey].items.push({ ...item, originalIndex: item.originalIndex !== undefined ? item.originalIndex : index });
    });

    return groups;
  };

  // Subtotale di listino di un insieme di articoli (prima degli sconti)
  const subtotalOf = (items) => {
    if (!items || items.length === 0) return 0;

    return items.reduce((total, item) => {
      const price = articleMetadata[item.productCode]?.price || 0;
      // Use quantity from root level, or fall back to processing.quantity for misura/progettazione
      const quantity = item.quantity || item.processing?.quantity || 0;
      return total + (price * quantity);
    }, 0);
  };

  // Subtotale, sconti e totale di un insieme di articoli (Sconto Tipico, poi Sconto Extra sul residuo)
  const totalsOf = (items) => {
    const subtotal = subtotalOf(items);
    const discountAmount = (subtotal * discountPercentage) / 100;
    const afterFirst = subtotal - discountAmount;
    const extraDiscountAmount = (afterFirst * extraDiscountPercentage) / 100;
    return { subtotal, discountAmount, afterFirst, extraDiscountAmount, total: afterFirst - extraDiscountAmount };
  };

  // Calculate subtotal (before discount)
  const calculateSubtotal = () => totalsOf(state.cartItems).subtotal;

  // Calculate Sconto Tipico amount (first discount)
  const calculateDiscountAmount = () => totalsOf(state.cartItems).discountAmount;

  // Calculate subtotal after Sconto Tipico (before Sconto Extra)
  const calculateSubtotalAfterFirstDiscount = () => totalsOf(state.cartItems).afterFirst;

  // Calculate Sconto Extra amount (second discount, applied after first)
  const calculateExtraDiscountAmount = () => totalsOf(state.cartItems).extraDiscountAmount;

  // Calculate total (after both discounts)
  const calculateTotal = () => totalsOf(state.cartItems).total;

  // ---- Varianti d'offerta (solo offerte) ----
  const cartList = Array.isArray(state.cartItems) ? state.cartItems : [];
  const variantsEnabled = orderType === 'offerta' && (state.variants || []).length > 0;
  const indexedItems = cartList.map((item, index) => ({ ...item, originalIndex: index }));
  const variantGroups = variantsEnabled
    ? (() => {
      const known = state.variants.map((v) => ({ id: v.id, title: v.title, items: indexedItems.filter((item) => item.variantId === v.id) }));
      const orphans = indexedItems.filter((item) => !state.variants.some((v) => v.id === item.variantId));
      return orphans.length > 0 ? [{ id: null, title: 'Senza variante', items: orphans }, ...known] : known;
    })()
    : [{ id: null, title: null, items: indexedItems }];
  const variantTotals = variantGroups.map((group) => ({ ...group, ...totalsOf(group.items) }));
  const averageOf = (key) => (variantTotals.length > 0 ? variantTotals.reduce((sum, g) => sum + g[key], 0) / variantTotals.length : 0);
  const round2 = (n) => Math.round(n * 100) / 100;

  // Add the current product to cart when component mounts
  useEffect(() => {
    // Only add to cart if there's a product code (meaning a product has been configured)
    // Check if the cart already has the main profile article (not just drainage articles)
    const hasMainProfileArticle = Array.isArray(state.cartItems) &&
      state.cartItems.some(item => !item.isStandaloneArticle && !item.isDrainageArticle);

    if (state.orderDetails.productCode && !state.orderDetails.addedToCart && !hasMainProfileArticle) {
      console.log('Auto-adding current product to cart');
      dispatch({ type: 'ADD_TO_CART' });
    }
  }, [state.orderDetails, state.cartItems, dispatch]);

  // Fetch article metadata (description, unit of measure, and price) from database
  useEffect(() => {
    const fetchArticleMetadata = async () => {
      if (!state.cartItems || state.cartItems.length === 0) return;

      const productCodes = state.cartItems
        .map(item => item.productCode)
        .filter(code => code && !articleMetadata[code]); // Only fetch if not already cached

      if (productCodes.length === 0) return;

      try {
        const metadata = {};
        await Promise.all(
          productCodes.map(async (code) => {
            try {
              const response = await api.getArticleById(code);
              if (response && response.article) {
                metadata[code] = {
                  description: response.article.Description || '',
                  unit: response.article.PriceListUoM || 'metri', // Default to 'metri' if not specified
                  price: parseFloat(response.article.Price) || 0 // Get price from database
                };
              }
            } catch (err) {
              console.warn(`Failed to fetch metadata for ${code}:`, err);
              // Keep going even if one fails
            }
          })
        );

        setArticleMetadata(prev => ({ ...prev, ...metadata }));
      } catch (error) {
        console.error('Error fetching article metadata:', error);
      }
    };

    fetchArticleMetadata();
  }, [state.cartItems]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handler for "NUOVO PRODOTTO" button - navigate to product categories
  const handleNewProduct = () => {
    // Reset the current order details but keep the cart items
    dispatch({
      type: 'UPDATE_ORDER',
      payload: {
        addedToCart: false,
        productCode: ''
      }
    });

    // Navigate to product categories
    navigate('/product-categories');
  };

  // Il nuovo documento non sostituira' piu' l'originale: va tolto il riferimento anche dalla bozza sul server
  const handleKeepOriginal = async () => {
    const confirm = await Swal.fire({
      icon: 'question',
      title: 'Mantenere l\'originale?',
      text: `${state.replaces.orderNumber} resterà valido e questo documento sarà un nuovo ordine a sé.`,
      showCancelButton: true,
      confirmButtonText: 'Sì, non annullare',
      cancelButtonText: 'No',
      confirmButtonColor: '#2563eb'
    });
    if (!confirm.isConfirmed) return;

    try {
      if (state.draftOrderId) {
        await api.updateDraftOrder(state.draftOrderId, { order_data: { ...state.orderDetails } });
      }
      dispatch({ type: 'CLEAR_REPLACES' });
    } catch (err) {
      console.error('Error detaching replaced order:', err);
      Swal.fire({ icon: 'error', title: 'Errore', text: 'Impossibile scollegare l\'ordine originale. Riprova.' });
    }
  };

  const escapeHtml = (text) => String(text).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const titleExists = (title, exceptId = null) =>
    (state.variants || []).some((v) => v.id !== exceptId && v.title.trim().toLowerCase() === title.trim().toLowerCase());

  // Aggiungi variante: un nuovo blocco alternativo dentro la stessa offerta
  const handleAddVariant = async () => {
    const existing = state.variants || [];
    const needsFirst = existing.length === 0 && cartList.length > 0;
    const nextNumber = existing.length + (needsFirst ? 2 : 1);

    const result = await Swal.fire({
      title: 'Aggiungi variante',
      html: `
        <div class="text-left">
          <p class="text-sm text-gray-600 mb-3">Una variante è un blocco alternativo di articoli nella stessa offerta (es. "Soluzione 1", "Soluzione 2"). Ogni variante ha il suo totale.</p>
          ${needsFirst ? `
            <label class="block text-sm font-medium text-gray-700 mb-1">Nome della prima variante <span class="text-gray-400">(articoli già nel carrello)</span></label>
            <input id="variant-first-title" class="swal2-input" style="margin:0 0 12px 0;width:100%" value="Variante 1" maxlength="80">
          ` : ''}
          <label class="block text-sm font-medium text-gray-700 mb-1">Nome della nuova variante</label>
          <input id="variant-title" class="swal2-input" style="margin:0;width:100%" value="Variante ${nextNumber}" maxlength="80">
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Crea variante',
      cancelButtonText: 'Annulla',
      confirmButtonColor: '#2563eb',
      preConfirm: () => {
        const firstTitle = needsFirst ? document.getElementById('variant-first-title').value.trim() : '';
        const title = document.getElementById('variant-title').value.trim();
        if ((needsFirst && !firstTitle) || !title) {
          Swal.showValidationMessage('Inserisci un nome per ogni variante');
          return false;
        }
        if (needsFirst && firstTitle.toLowerCase() === title.toLowerCase()) {
          Swal.showValidationMessage('I nomi delle varianti devono essere diversi');
          return false;
        }
        if (titleExists(title) || (needsFirst && titleExists(firstTitle))) {
          Swal.showValidationMessage('Esiste già una variante con questo nome');
          return false;
        }
        return { firstTitle, title };
      }
    });

    if (!result.isConfirmed || !result.value) return;

    dispatch({
      type: 'CREATE_VARIANT',
      payload: {
        id: newVariantId(),
        title: result.value.title,
        adoptFirst: needsFirst ? { id: newVariantId(), title: result.value.firstTitle } : undefined
      }
    });

    const next = await Swal.fire({
      icon: 'success',
      title: 'Variante creata',
      html: `Vuoi aggiungere subito gli articoli a <strong>${escapeHtml(result.value.title)}</strong>?`,
      showCancelButton: true,
      confirmButtonText: 'Aggiungi articoli',
      cancelButtonText: 'Più tardi',
      confirmButtonColor: '#2563eb'
    });
    if (next.isConfirmed) handleNewProduct();
  };

  const handleRenameVariant = async (group) => {
    const result = await Swal.fire({
      title: 'Rinomina variante',
      input: 'text',
      inputValue: group.title,
      inputAttributes: { maxlength: 80 },
      showCancelButton: true,
      confirmButtonText: 'Salva',
      cancelButtonText: 'Annulla',
      confirmButtonColor: '#2563eb',
      inputValidator: (value) => {
        if (!value || !value.trim()) return 'Inserisci un nome';
        if (titleExists(value, group.id)) return 'Esiste già una variante con questo nome';
        return null;
      }
    });
    if (result.isConfirmed) {
      dispatch({ type: 'RENAME_VARIANT', payload: { id: group.id, title: result.value.trim() } });
    }
  };

  const handleDuplicateVariant = async (group) => {
    let suggestion = `${group.title} (copia)`;
    let n = 2;
    while (titleExists(suggestion)) { suggestion = `${group.title} (copia ${n})`; n += 1; }

    const result = await Swal.fire({
      title: 'Duplica variante',
      text: `Copia tutti i ${group.items.length} articoli di "${group.title}" in una nuova variante.`,
      input: 'text',
      inputValue: suggestion,
      inputAttributes: { maxlength: 80 },
      showCancelButton: true,
      confirmButtonText: 'Duplica',
      cancelButtonText: 'Annulla',
      confirmButtonColor: '#2563eb',
      inputValidator: (value) => {
        if (!value || !value.trim()) return 'Inserisci un nome';
        if (titleExists(value)) return 'Esiste già una variante con questo nome';
        return null;
      }
    });
    if (result.isConfirmed) {
      dispatch({ type: 'DUPLICATE_VARIANT', payload: { sourceId: group.id, newId: newVariantId(), title: result.value.trim() } });
    }
  };

  const handleRemoveVariant = async (group) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Elimina variante',
      html: `Verrà eliminata <strong>${escapeHtml(group.title)}</strong> con tutti i suoi <strong>${group.items.length}</strong> articoli.<br>L'operazione non si può annullare.`,
      showCancelButton: true,
      confirmButtonText: 'Sì, elimina',
      cancelButtonText: 'Annulla',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280'
    });
    if (result.isConfirmed) dispatch({ type: 'REMOVE_VARIANT', payload: { id: group.id } });
  };

  const handleClearVariants = async () => {
    const result = await Swal.fire({
      icon: 'question',
      title: 'Rimuovere le varianti?',
      text: 'Gli articoli restano nell\'offerta, ma non saranno più divisi in varianti.',
      showCancelButton: true,
      confirmButtonText: 'Sì, rimuovi',
      cancelButtonText: 'Annulla',
      confirmButtonColor: '#2563eb'
    });
    if (result.isConfirmed) dispatch({ type: 'CLEAR_VARIANTS' });
  };

  // Aggiunge articoli a una variante precisa: la rende attiva e apre la scelta prodotti
  const handleAddItemsToVariant = (group) => {
    if (group.id) dispatch({ type: 'SET_ACTIVE_VARIANT', payload: group.id });
    handleNewProduct();
  };

  // Sposta un articolo (o tutto il suo kit) in un'altra variante
  const handleMoveItem = async (item) => {
    const targets = (state.variants || []).filter((v) => v.id !== item.variantId);
    if (targets.length === 0) return;

    const options = {};
    targets.forEach((v) => { options[v.id] = v.title; });
    const pick = await Swal.fire({
      title: 'Sposta in un\'altra variante',
      html: `<p class="text-sm text-gray-600"><strong>${escapeHtml(item.productCode || 'Articolo')}</strong></p>`,
      input: 'select',
      inputOptions: options,
      inputPlaceholder: 'Scegli la variante',
      showCancelButton: true,
      confirmButtonText: 'Avanti',
      cancelButtonText: 'Annulla',
      confirmButtonColor: '#2563eb',
      inputValidator: (value) => (value ? null : 'Scegli una variante')
    });
    if (!pick.isConfirmed) return;

    const kitIndices = item.profileSessionId
      ? indexedItems.filter((i) => i.profileSessionId === item.profileSessionId && i.variantId === item.variantId).map((i) => i.originalIndex)
      : [item.originalIndex];

    let indices = [item.originalIndex];
    if (kitIndices.length > 1) {
      const scope = await Swal.fire({
        title: 'Cosa vuoi spostare?',
        text: `Questo articolo fa parte di un kit di ${kitIndices.length} articoli.`,
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: `Tutto il kit (${kitIndices.length})`,
        denyButtonText: 'Solo questo articolo',
        cancelButtonText: 'Annulla',
        confirmButtonColor: '#2563eb',
        denyButtonColor: '#6b7280'
      });
      if (scope.isDismissed) return;
      if (scope.isConfirmed) indices = kitIndices;
    }

    dispatch({ type: 'MOVE_ITEMS_TO_VARIANT', payload: { indices, variantId: pick.value } });
  };

  // Handler for "BOZZA" button - save the order as a draft
  const handleSaveAsDraft = async () => {
    if (!user) {
      setError('Utente non autenticato');
      return;
    }

    // Check if there are items in the cart
    const hasCartItems = Array.isArray(state.cartItems) && state.cartItems.length > 0;

    if (!hasCartItems) {
      Swal.fire({
        title: 'Carrello vuoto',
        text: 'Non ci sono prodotti da salvare come bozza.',
        icon: 'warning'
      });
      return;
    }

    // If customer is not selected, use the logged-in client as the customer
    const customerId = state.customer ? state.customer.CustSupp : user.id;

    setIsLoading(true);
    setError(null);

    try {
      // Determine who is creating the draft (agent or client)
      const isAgentUser = user?.role === 'agent' || user?.role === 'head_agent';
      // For agents, user.id is the Salesperson code
      const createdById = isAgentUser ? user?.id : customerId;
      const createdByType = isAgentUser ? 'agent' : 'client';

      const draftOrderData = {
        customer_id: customerId,
        created_by_id: createdById,
        created_by_type: createdByType,
        // Una bozza che sostituisce un ordine deve ricordarlo, altrimenti riprendendola l'originale non verrebbe annullato
        order_data: state.replaces
          ? { ...state.orderDetails, originalOrderNumber: state.replaces.orderNumber }
          : state.orderDetails,
        cart_items: state.cartItems
      };

      // If we already have a draft order ID, update it
      if (state.draftOrderId) {
        await api.updateDraftOrder(state.draftOrderId, draftOrderData);
      } else {
        // Otherwise create a new draft order
        const result = await api.createDraftOrder(draftOrderData);
        dispatch({ type: 'SET_DRAFT_ORDER_ID', payload: result.id });
      }

      // Empty the cart after saving the draft
      dispatch({ type: 'RESET_CART' });

      const typeLabel = orderType === 'offerta' ? 'Offerta' : 'Ordine';
      Swal.fire({
        title: 'Successo!',
        text: `${typeLabel} salvat${orderType === 'offerta' ? 'a' : 'o'} come bozza e carrello svuotato`,
        icon: 'success',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3085d6'
      }).then(() => {
        navigate('/dashboard');
      });
    } catch (err) {
      console.error('Error saving draft order:', err);
      setError('Errore durante il salvataggio della bozza');
    } finally {
      setIsLoading(false);
    }
  };

  // Handler for "COMPLETA" button - complete the order
  const handleCompleteOrder = async () => {
    if (Array.isArray(state.cartItems) && state.cartItems.length === 0) {
      Swal.fire({
        title: 'Carrello vuoto',
        text: 'Non ci sono prodotti nel carrello per completare l\'ordine.',
        icon: 'warning'
      });
      return;
    }

    if (variantsEnabled) {
      const emptyVariant = variantGroups.find((g) => g.id && g.items.length === 0);
      if (emptyVariant) {
        Swal.fire({
          icon: 'warning',
          title: 'Variante vuota',
          html: `La variante <strong>${escapeHtml(emptyVariant.title)}</strong> non ha articoli.<br>Aggiungi articoli oppure eliminala prima di inviare l'offerta.`
        });
        return;
      }
    }

    // Ask for delivery date and notes
    const { value: formValues } = await Swal.fire({
      title: orderType === 'offerta' ? 'Completa i dettagli dell\'offerta' : 'Completa i dettagli dell\'ordine',
      html: `
        <div class="text-left space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Eventuale data spedizione desiderata<br><i>Da confermare dall'ufficio ordini</i></label>
            <input type="date" id="delivery-date" class="w-full p-2 border border-gray-300 rounded-md" required>
          </div>
          <br>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Indirizzo di consegna diverso?</label>
            <div class="grid grid-cols-2 gap-2 mb-2">
              <input type="text" id="delivery-address" placeholder="Indirizzo" class="w-full p-2 border border-gray-300 rounded-md">
              <input type="text" id="delivery-zip" placeholder="CAP" class="w-full p-2 border border-gray-300 rounded-md">
            </div>
            <div class="grid grid-cols-2 gap-2">
              <input type="text" id="delivery-city" placeholder="Città" class="w-full p-2 border border-gray-300 rounded-md">
              <input type="text" id="delivery-province" placeholder="Provincia" class="w-full p-2 border border-gray-300 rounded-md">
            </div>
          </div>
          <br>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Note aggiuntive (opzionale)</label>
            <textarea id="additional-notes" class="w-full p-2 border border-gray-300 rounded-md h-20" placeholder="Inserisci eventuali note..."></textarea>
          </div>
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: orderType === 'offerta' ? 'Conferma Offerta' : 'Conferma Ordine',
      cancelButtonText: 'Annulla',
      preConfirm: () => {
        const deliveryDate = document.getElementById('delivery-date').value;
        const deliveryAddress = document.getElementById('delivery-address').value;
        const deliveryZip = document.getElementById('delivery-zip').value;
        const deliveryCity = document.getElementById('delivery-city').value;
        const deliveryProvince = document.getElementById('delivery-province').value;
        const notes = document.getElementById('additional-notes').value;

        return {
          deliveryDate: deliveryDate,
          deliveryAddress: deliveryAddress,
          deliveryZip: deliveryZip,
          deliveryCity: deliveryCity,
          deliveryProvince: deliveryProvince,
          notes: notes
        };
      }
    });

    if (!formValues) return; // User cancelled

    // Second confirmation before final order submission
    const confirmResult = await Swal.fire({
      title: 'Conferma definitiva',
      text: orderType === 'offerta'
        ? 'Sei sicuro di voler inviare l\'offerta? Una volta confermata non potrà essere modificata.'
        : 'Sei sicuro di voler inviare l\'ordine? Una volta confermato non potrà essere modificato.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#d33',
      confirmButtonText: orderType === 'offerta' ? 'Sì, invia offerta' : 'Sì, invia ordine',
      cancelButtonText: 'Annulla'
    });

    if (!confirmResult.isConfirmed) return;

    setIsLoading(true);
    setError(null);

    try {
      let result;

      // Calculate pricing totals to include in the order
      // Con le varianti (alternative tra loro) i valori generali sono la media delle varianti
      const subtotal = variantsEnabled ? round2(averageOf('subtotal')) : calculateSubtotal();
      const discountAmount = variantsEnabled ? round2(averageOf('discountAmount')) : calculateDiscountAmount(); // Sconto Tipico amount
      const extraDiscountAmount = variantsEnabled ? round2(averageOf('extraDiscountAmount')) : calculateExtraDiscountAmount(); // Sconto Extra amount
      const total = variantsEnabled ? round2(averageOf('total')) : calculateTotal();
      const variantsPayload = variantsEnabled
        ? variantTotals.map((g) => ({
          id: g.id || 'senza-variante',
          title: g.title,
          itemCount: g.items.length,
          subtotal: round2(g.subtotal),
          discountAmount: round2(g.discountAmount),
          extraDiscountAmount: round2(g.extraDiscountAmount),
          total: round2(g.total)
        }))
        : null;

      if (state.draftOrderId) {
        // Il server completa la bozza leggendo gli articoli salvati: allinea la bozza al carrello a schermo
        // (quantita' modificate, articoli rimossi, varianti) prima di completarla
        await api.updateDraftOrder(state.draftOrderId, { cart_items: state.cartItems });

        // If this is a draft order, complete it
        result = await api.completeDraftOrder(state.draftOrderId, {
          deliveryDate: formValues.deliveryDate,
          deliveryAddress: formValues.deliveryAddress,
          deliveryZip: formValues.deliveryZip,
          deliveryCity: formValues.deliveryCity,
          deliveryProvince: formValues.deliveryProvince,
          notes: formValues.notes,
          subtotal: subtotal,
          discountPercentage: discountPercentage, // Sconto Tipico %
          discountAmount: discountAmount, // Sconto Tipico amount
          extraDiscountPercentage: extraDiscountPercentage, // Sconto Extra %
          extraDiscountAmount: extraDiscountAmount, // Sconto Extra amount
          total: total,
          orderType: orderType, // Pass order type to backend
          sentByName: user?.name || 'N/A',
          sentByRole: user?.role || 'N/A',
          variants: variantsPayload,
          replacesOrderNumber: state.replaces?.orderNumber,
          requester: { role: user?.role, userId: user?.id, userType: user?.userType }
        });
        dispatch({ type: 'SET_ORDER_NUMBER', payload: result.order_number });
      } else {
        // Otherwise submit as a new order with cart items
        const orderData = {
          cartItems: state.cartItems,
          customer: state.customer?.CustSupp,
          deliveryDate: formValues.deliveryDate,
          deliveryAddress: formValues.deliveryAddress,
          deliveryZip: formValues.deliveryZip,
          deliveryCity: formValues.deliveryCity,
          deliveryProvince: formValues.deliveryProvince,
          notes: formValues.notes,
          subtotal: subtotal,
          discountPercentage: discountPercentage, // Sconto Tipico %
          discountAmount: discountAmount, // Sconto Tipico amount
          extraDiscountPercentage: extraDiscountPercentage, // Sconto Extra %
          extraDiscountAmount: extraDiscountAmount, // Sconto Extra amount
          total: total,
          orderType: orderType, // Pass order type to backend
          sentByName: user?.name || 'N/A',
          sentByRole: user?.role || 'N/A',
          ...(variantsPayload ? { variants: variantsPayload } : {}),
          ...(state.replaces
            ? {
              replacesOrderNumber: state.replaces.orderNumber,
              replacesDraftId: state.replaces.draftId,
              requester: { role: user?.role, userId: user?.id, userType: user?.userType }
            }
            : {})
        };
        result = await api.submitOrder(orderData);
        dispatch({ type: 'SET_ORDER_NUMBER', payload: result.id });
      }

      // Empty the cart after completing the order
      dispatch({ type: 'RESET_CART' });

      // Show success message with delivery date and email info
      const emailStatus = result.email_sent ? '✅ Email di notifica inviata' : '⚠️ Email non inviata (errore di sistema)';
      const successTitle = orderType === 'offerta' ? 'Offerta completata!' : 'Ordine completato!';
      const successMessage = orderType === 'offerta'
        ? 'La tua offerta è stata inviata con successo.'
        : 'Il tuo ordine è stato inviato con successo.';
      const numberLabel = orderType === 'offerta' ? 'Numero offerta:' : 'Numero ordine:';

      Swal.fire({
        title: successTitle,
        html: `
          <div class="text-center">
            <p class="mb-2">${successMessage}</p>
            <p class="mb-2"><strong>${numberLabel}</strong> ${result.order_number || 'N/A'}</p>
            <p class="mb-2"><strong>Data di consegna richiesta:</strong> ${new Date(formValues.deliveryDate).toLocaleDateString('it-IT')}</p>
            <p class="text-sm text-gray-600">${emailStatus}</p>
          </div>
        `,
        icon: 'success',
        confirmButtonText: 'OK'
      }).then(() => {
        navigate('/confirmation');
      });

    } catch (err) {
      console.error('Error completing order:', err);
      setError('Errore durante il completamento dell\'ordine');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header
        title={orderType === 'offerta' ? 'Riepilogo Offerta' : 'Riepilogo Ordine'}
        backText="Configurazione"
        backTo="/order"
      />

      <div className="container mx-auto px-4 py-6">
        <div className="max-w-lg mx-auto bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold mb-4">{orderType === 'offerta' ? 'Offerta' : 'Ordine'}</h2>

          {state.replaces && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900">
              <p className="font-semibold">Stai modificando {state.replaces.orderNumber}</p>
              <p className="mt-1 text-blue-800">Quando invii, questo {orderType === 'offerta' ? 'documento' : 'ordine'} sostituisce l'originale, che viene annullato con una mail di annullamento.</p>
              <button
                type="button"
                onClick={handleKeepOriginal}
                className="mt-2 text-xs font-semibold underline underline-offset-2 text-blue-700 hover:text-blue-900"
              >
                Non annullare l'originale
              </button>
            </div>
          )}

          {/* Display Customer Information */}
          {state.customer && (
            <div className="mb-6 p-4 bg-gradient-to-r from-red-50 to-red-100 rounded-lg border-2 border-red-200">
              <div className="flex items-center mb-2">
                <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <h3 className="font-semibold text-red-900">Cliente Selezionato</h3>
                {state.customer.isProspect && (
                  <span className="ml-2 text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-300 px-2 py-0.5 rounded-full">
                    Nuovo cliente · non in anagrafica
                  </span>
                )}
              </div>
              <div className="ml-7 space-y-1">
                <p className="font-mono font-bold text-lg text-red-800">{state.customer.CustSupp}</p>
                <p className="text-gray-800 font-medium">{state.customer.CompanyName}</p>
                {state.customer.isProspect && (
                  <div className="text-sm text-gray-700 space-y-0.5">
                    {(state.customer.prospectNome || state.customer.prospectCognome) && (
                      <p><span className="font-medium">Referente:</span> {state.customer.prospectNome} {state.customer.prospectCognome}</p>
                    )}
                    {state.customer.MAIL && <p><span className="font-medium">Email:</span> {state.customer.MAIL}</p>}
                    {state.customer.TELEFONO && <p><span className="font-medium">Telefono:</span> {state.customer.TELEFONO}</p>}
                  </div>
                )}
                {state.customer.TaxIdNumber && (
                  <p className="text-sm text-gray-600">P.IVA: {state.customer.TaxIdNumber}</p>
                )}
                <div className="flex gap-3 mt-2 text-sm">
                  {state.customer.PriceList && (
                    <span className="bg-red-100 text-red-800 px-2 py-1 rounded">
                      Listino: {state.customer.PriceList}
                    </span>
                  )}
                  {state.customer.NameSalesPerson && (
                    <span className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded">
                      Agente: {state.customer.NameSalesPerson}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Display cart items grouped by section */}
          <div className="mb-6">
            {cartList.length > 0 || variantsEnabled ? (
              <div className="space-y-5">
                {variantTotals.map((group) => {
                  const variantNumber = (state.variants || []).findIndex((v) => v.id === group.id) + 1;
                  const isActiveVariant = !!group.id && group.id === state.activeVariantId;
                  return (
                  <div
                    key={group.id || 'base'}
                    className={variantsEnabled ? `rounded-xl border-2 p-3 ${isActiveVariant ? 'border-blue-500 bg-blue-50/60 ring-2 ring-blue-200' : 'border-blue-200 bg-blue-50/30'}` : ''}
                  >
                    {variantsEnabled && (
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {variantNumber > 0 && (
                            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center">{variantNumber}</span>
                          )}
                          <div className="min-w-0">
                            <h3 className="font-bold text-blue-900 truncate" title={group.title}>{group.title}</h3>
                            <p className="text-xs text-blue-700">
                              {group.items.length} {group.items.length === 1 ? 'articolo' : 'articoli'}
                              {isActiveVariant && ' • qui vengono aggiunti i nuovi articoli'}
                            </p>
                          </div>
                        </div>
                        {group.id && (
                          <div className="flex items-center gap-1">
                            <button onClick={() => handleRenameVariant(group)} className="p-2 rounded-lg text-blue-700 hover:bg-blue-100" title="Rinomina variante">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                            </button>
                            {group.items.length > 0 && (
                              <button onClick={() => handleDuplicateVariant(group)} className="p-2 rounded-lg text-blue-700 hover:bg-blue-100" title="Duplica variante">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                              </button>
                            )}
                            {(state.variants || []).length > 1 ? (
                              <button onClick={() => handleRemoveVariant(group)} className="p-2 rounded-lg text-red-600 hover:bg-red-100" title="Elimina variante">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                              </button>
                            ) : (
                              <button onClick={handleClearVariants} className="px-2 py-1 rounded-lg text-xs font-semibold text-blue-700 hover:bg-blue-100" title="Mantieni gli articoli senza suddivisione in varianti">
                                Rimuovi varianti
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="space-y-4">
                {Object.entries(groupItemsBySection(group.items))
                  // Sort sections by the canonical rank of their first (lowest-rank) item
                  .map(([sectionKey, section]) => {
                    const sortedItems = [...section.items].sort((a, b) => getItemSortRank(a) - getItemSortRank(b));
                    return [sectionKey, { ...section, items: sortedItems, sortKey: sortedItems.length ? getItemSortRank(sortedItems[0]) : 99 }];
                  })
                  .sort(([, a], [, b]) => a.sortKey - b.sortKey)
                  .map(([sectionKey, section]) => (
                  <div key={sectionKey} className="border-2 border-red-200 rounded-lg p-4 bg-red-50">
                    {/* Section Header */}
                    <div className="flex items-center mb-3">
                      <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                      <h3 className="font-semibold text-red-800">{section.title}</h3>
                      <span className="ml-2 text-sm text-red-600 bg-red-200 px-2 py-1 rounded-full">
                        {section.items.length} {section.items.length === 1 ? 'prodotto' : 'prodotti'}
                      </span>
                    </div>

                    {/* Items in this section */}
                    <div className="space-y-3">
                      {section.items.map((item) => (
                        <div key={item.originalIndex} className="p-3 bg-white rounded-lg border border-gray-200">
                          {/* Debug info - remove in production */}
                          {console.log('OrderSummary - Cart item:', {
                            productCode: item.productCode,
                            hasProcessing: !!item.processing,
                            processingSupplyType: item.processing?.supplyType,
                            hasWizardSteps: !!item.wizardSteps,
                            wizardStepsProcessing: item.wizardSteps?.processing
                          })}
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <p className="font-mono font-semibold text-lg">{item.productCode || 'Prodotto senza codice'}</p>

                              {/* Price display */}
                              {articleMetadata[item.productCode]?.price !== undefined && (
                                <div className="mt-1 text-sm text-gray-600">
                                  <span className="font-medium">Prezzo unitario:</span> € {formatNumber(articleMetadata[item.productCode].price)} / {articleMetadata[item.productCode]?.unit || 'unità'}
                                  {(item.quantity || item.processing?.quantity) && (
                                    <span className="ml-3">
                                      <br />
                                      <span className="font-medium">Totale:</span> € {formatNumber(articleMetadata[item.productCode].price * (item.quantity || item.processing?.quantity || 0))}
                                    </span>
                                  )}
                                </div>
                              )}

                              {/* Enhanced display based on supply type - only for main products, not drainage articles or ricambi */}
                              {item.processing && item.processing.supplyType && !item.isDrainageArticle && !item.isRicambioItem ? (
                                <div className="mt-2 space-y-2">
                                  {/* Supply Type Badge */}
                                  <div className="flex items-center space-x-2">
                                    <span className={`inline-block text-xs px-3 py-1 rounded-full font-medium ${item.processing.supplyType === 'barre' ? 'bg-red-100 text-red-800' :
                                      item.processing.supplyType === 'misura' ? 'bg-green-100 text-green-800' :
                                        item.processing.supplyType === 'ricambi' ? 'bg-orange-100 text-orange-800' :
                                          'bg-purple-100 text-purple-800'
                                      }`}>
                                      {getSupplyTypeLabel(item.processing.supplyType)}
                                    </span>
                                    {item.processing.needsDrainage && (
                                      <span className="inline-block text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full">
                                        Con Scarico Acqua
                                      </span>
                                    )}
                                  </div>

                                  {/* Fornitura a Barre */}
                                  {item.processing.supplyType === 'barre' && (
                                    <div className="text-sm text-gray-700">
                                      {item.processing.barGroups && item.processing.barGroups.length > 1 ? (
                                        <>
                                          <p className="font-medium mb-1">Barre configurate:</p>
                                          <div className="bg-gray-50 rounded p-2 space-y-1">
                                            {item.processing.barGroups.map((g, idx) => (
                                              <div key={idx} className="text-xs">
                                                • {g.quantity} {g.quantity === 1 ? 'barra' : 'barre'} da {g.barLength} metri ({g.quantity * g.barLength} mt)
                                              </div>
                                            ))}
                                            <div className="pt-1 mt-1 border-t border-gray-300 text-xs font-medium">
                                              Totale: {item.processing.barGroups.reduce((s, g) => s + g.quantity, 0)} barre — {item.processing.barGroups.reduce((s, g) => s + g.quantity * g.barLength, 0)} metri
                                            </div>
                                          </div>
                                        </>
                                      ) : (
                                        <>
                                          <p className="font-medium">
                                            Quantità: {item.processing.quantity || item.quantity || 0} {articleMetadata[item.productCode]?.unit || item.unit || 'metri'}
                                          </p>
                                          <p className="text-xs text-gray-600">
                                            ({Math.floor((item.processing.quantity || item.quantity || 0) / (item.processing.barLength || 3))} {Math.floor((item.processing.quantity || item.quantity || 0) / (item.processing.barLength || 3)) === 1 ? 'barra' : 'barre'} da {item.processing.barLength || 'N/A'} metri)
                                          </p>
                                        </>
                                      )}
                                    </div>
                                  )}

                                  {/* Fornitura a Misura */}
                                  {item.processing.supplyType === 'misura' && item.processing.customBars && (
                                    <div className="text-sm text-gray-700">
                                      <p className="font-medium mb-1">
                                        Quantità: {item.processing.quantity || item.quantity || 0} {articleMetadata[item.productCode]?.unit || item.unit || 'metri'}
                                      </p>
                                      <p className="font-medium mb-1 text-xs">
                                        Barre personalizzate:
                                      </p>
                                      <div className="bg-gray-50 rounded p-2 space-y-1">
                                        {item.processing.customBars.map((bar, idx) => (
                                          <div key={idx} className="text-xs">
                                            • {bar.quantity || 0} {bar.quantity === 1 ? 'barra' : 'barre'} da {bar.length || 0}mm
                                          </div>
                                        ))}
                                        <div className="pt-2 mt-2 border-t border-gray-300 text-xs">
                                          Totale: {item.processing.customBars.reduce((sum, bar) => sum + (bar.quantity || 0), 0)} barre
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* Fornitura con Progettazione */}
                                  {item.processing.supplyType === 'progettazione' && (
                                    <div className="text-sm text-gray-700">
                                      <p className="font-medium mb-1">
                                        Quantità: {item.processing.quantity || item.quantity || 0} {articleMetadata[item.productCode]?.unit || item.unit || 'metri'}
                                      </p>
                                      <div className="space-y-1">
                                        {item.processing.photos && item.processing.photos.length > 0 && (
                                          <p><span className="font-medium">File caricati:</span> {item.processing.photos.length} foto</p>
                                        )}
                                        {item.processing.customBars && item.processing.customBars.length > 0 && (
                                          <p><span className="font-medium">Barre specificate:</span> {item.processing.customBars.length}</p>
                                        )}
                                        {/* Additional project fields */}
                                        {(item.processing.metriIndicativi || item.processing.vuoiGiunzioni || item.processing.vuoiCorrimano || item.processing.vuoiTappi || item.processing.vuoiSpessori || item.processing.vuoiCoprifilo || item.processing.vuoiLed) && (
                                          <div className="bg-blue-50 rounded p-2 mt-2 space-y-1">
                                            {item.processing.metriIndicativi && (
                                              <p className="text-xs"><span className="font-medium">Metri indicativi:</span> {item.processing.metriIndicativi}</p>
                                            )}
                                            {item.processing.vuoiGiunzioni && (
                                              <p className="text-xs"><span className="font-medium">Giunzioni:</span> Si{item.processing.giunzioniDettaglio ? ` - ${item.processing.giunzioniDettaglio}` : ''}</p>
                                            )}
                                            {item.processing.vuoiCorrimano && (
                                              <p className="text-xs"><span className="font-medium">Corrimano:</span> Si{item.processing.corrimanoDettaglio ? ` - ${item.processing.corrimanoDettaglio}` : ''}</p>
                                            )}
                                            {item.processing.vuoiTappi && (
                                              <p className="text-xs"><span className="font-medium">Tappi:</span> Si</p>
                                            )}
                                            {item.processing.vuoiSpessori && (
                                              <p className="text-xs"><span className="font-medium">Spessori:</span> Si</p>
                                            )}
                                            {item.processing.vuoiCoprifilo && (
                                              <p className="text-xs"><span className="font-medium">Coprifilo:</span> Si{item.processing.coprifiloDettaglio ? ` - ${item.processing.coprifiloDettaglio}` : ''}</p>
                                            )}
                                            {item.processing.vuoiLed && (
                                              <p className="text-xs"><span className="font-medium">Led:</span> Si</p>
                                            )}
                                          </div>
                                        )}
                                        {item.processing.notes && (
                                          <div className="bg-gray-50 rounded p-2 mt-2">
                                            <p className="font-medium">Note progetto:</p>
                                            <p className="text-xs">{item.processing.notes.substring(0, 100)}{item.processing.notes.length > 100 ? '...' : ''}</p>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Show RAL code for V1/V2 special finishes (for items with processing) */}
                                  {item.ralCode && (
                                    <div className="mt-2">
                                      <span className="inline-block text-xs px-3 py-1 rounded-full font-medium bg-blue-100 text-blue-800">
                                        Codice RAL: {item.ralCode}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              ) : item.isAlbaItem ? (
                                <div className="text-sm text-gray-600 mt-1">
                                  <span className="inline-block text-xs px-3 py-1 rounded-full font-medium bg-amber-100 text-amber-800 mb-2">Alba</span>
                                  <p>Quantità: {item.quantity || 0} pz</p>
                                  {item.description && <p className="text-xs text-gray-500">{item.description}</p>}
                                  {item.price && <p className="text-sm mt-1">Prezzo: &euro; {(item.price * (item.quantity || 0)).toFixed(2)}</p>}
                                  {item.ralCode && (
                                    <span className="inline-block text-xs px-3 py-1 rounded-full font-medium bg-blue-100 text-blue-800 mt-2">
                                      RAL {item.ralCode}
                                    </span>
                                  )}
                                </div>
                              ) : item.isPensilineKit ? (
                                <div className="text-sm text-gray-600 mt-1">
                                  <span className={`inline-block text-xs px-3 py-1 rounded-full font-medium mb-2 ${item.isPensilineProgettazione ? 'bg-purple-100 text-purple-800' : 'bg-indigo-100 text-indigo-800'}`}>
                                    {item.isPensilineProgettazione ? 'Linea 3 Progettazione' : 'Linea 3 Kit'}
                                  </span>
                                  {item.description && <p className="text-xs text-gray-500">{item.description}</p>}
                                  {item.ralCode && (
                                    <span className="inline-block text-xs px-3 py-1 rounded-full font-medium bg-blue-100 text-blue-800 mt-2">
                                      RAL {item.ralCode}
                                    </span>
                                  )}
                                  {item.isPensilineProgettazione && item.processing && (
                                    <div className="mt-2 space-y-2">
                                      {item.processing.uploadedFiles && item.processing.uploadedFiles.length > 0 && (
                                        <p className="text-xs"><span className="font-medium">File caricati:</span> {item.processing.uploadedFiles.length}</p>
                                      )}
                                      {item.processing.notes && (
                                        <div className="bg-gray-50 rounded p-2">
                                          <p className="font-medium text-xs">Note progetto:</p>
                                          <p className="text-xs">{item.processing.notes.substring(0, 100)}{item.processing.notes.length > 100 ? '...' : ''}</p>
                                        </div>
                                      )}
                                      {(item.processing.metriIndicativi || item.processing.vuoiGiunzioni || item.processing.vuoiCorrimano || item.processing.vuoiTappi || item.processing.vuoiSpessori || item.processing.vuoiCoprifilo || item.processing.vuoiLed) && (
                                        <div className="bg-blue-50 rounded p-2 space-y-1">
                                          {item.processing.metriIndicativi && <p className="text-xs"><span className="font-medium">Metri indicativi:</span> {item.processing.metriIndicativi}</p>}
                                          {item.processing.vuoiGiunzioni && <p className="text-xs"><span className="font-medium">Giunzioni:</span> Si{item.processing.giunzioniDettaglio ? ` - ${item.processing.giunzioniDettaglio}` : ''}</p>}
                                          {item.processing.vuoiCorrimano && <p className="text-xs"><span className="font-medium">Corrimano:</span> Si{item.processing.corrimanoDettaglio ? ` - ${item.processing.corrimanoDettaglio}` : ''}</p>}
                                          {item.processing.vuoiTappi && <p className="text-xs"><span className="font-medium">Tappi:</span> Si</p>}
                                          {item.processing.vuoiSpessori && <p className="text-xs"><span className="font-medium">Spessori:</span> Si</p>}
                                          {item.processing.vuoiCoprifilo && <p className="text-xs"><span className="font-medium">Coprifilo:</span> Si{item.processing.coprifiloDettaglio ? ` - ${item.processing.coprifiloDettaglio}` : ''}</p>}
                                          {item.processing.vuoiLed && <p className="text-xs"><span className="font-medium">Led:</span> Si</p>}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ) : item.isSferaNItem ? (
                                <div className="text-sm text-gray-600 mt-1">
                                  <span className="inline-block text-xs px-3 py-1 rounded-full font-medium bg-teal-100 text-teal-800 mb-2">Sfera-N</span>
                                  <p>Quantità: {item.quantity || 0} pz</p>
                                  {item.description && <p className="text-xs text-gray-500">{item.description}</p>}
                                  {item.price && <p className="text-sm mt-1">Prezzo: &euro; {(item.price * (item.quantity || 0)).toFixed(2)}</p>}
                                  {item.sferaNNote && (
                                    <div className="bg-blue-50 rounded p-2 mt-2">
                                      <p className="text-xs"><span className="font-medium">Note:</span> {item.sferaNNote}</p>
                                    </div>
                                  )}
                                </div>
                              ) : !item.isRicambioItem ? (
                                // Fallback for items without processing data (but not ricambi)
                                <div className="text-sm text-gray-600 mt-1">
                                  <p>Quantità: {item.quantity || 0} {articleMetadata[item.productCode]?.unit || item.unit || (item.isAccessoryItem ? 'metri' : '')}</p>
                                  {item.length && (
                                    <p>Lunghezza: {item.length}mm</p>
                                  )}

                                  {/* Show custom bars for accessories with "misura" supply type */}
                                  {item.isAccessoryItem && item.supplyType === 'misura' && item.customBars && item.customBars.length > 0 && (
                                    <div className="mt-2">
                                      <p className="font-medium mb-1">Barre personalizzate:</p>
                                      <div className="bg-gray-50 rounded p-2 space-y-1">
                                        {item.customBars.map((bar, idx) => (
                                          <div key={idx}>
                                            • {bar.quantity || 0} {bar.quantity === 1 ? 'barra' : 'barre'} da {bar.length || 0}mm
                                          </div>
                                        ))}
                                        <div className="pt-2 mt-2 border-t border-gray-300 font-medium">
                                          Totale: {item.customBars.reduce((sum, bar) => sum + (bar.quantity || 0), 0)} barre
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* Show RAL code for V1/V2 special finishes */}
                                  {item.ralCode && (
                                    <div className="mt-2">
                                      <span className="inline-block text-xs px-3 py-1 rounded-full font-medium bg-blue-100 text-blue-800">
                                        Codice RAL: {item.ralCode}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              ) : null}

                              {/* Show category for automatic products */}
                              {item.category === 'automatic' && (
                                <span className="inline-block text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded mt-2">
                                  Aggiunta Automatica
                                </span>
                              )}

                              {/* Legacy support for old processing format */}
                              {item.productCode && item.productCode.startsWith('N') &&
                                item.processing && item.processing.currentBar && !item.processing.supplyType && (
                                  <div className="mt-1 text-sm text-gray-600">
                                    {item.processing.currentBar.barLength &&
                                      <p>Barre da {item.processing.currentBar.barLength} metri</p>}
                                    {item.processing.bars && item.processing.bars.length > 0 && item.processing.bars.map((bar, idx) => (
                                      <p key={idx}>
                                        {bar.quantity} {bar.quantity === 1 ? 'barra' : 'barre'} da {bar.barLength} metri
                                      </p>
                                    ))}
                                  </div>
                                )}

                              {/* Show Passo information for drainage articles - exclude automatic additions like N6-TF-* */}
                              {item.isDrainageArticle && !item.isAutomaticAddition && (
                                <div className="mt-1 text-sm text-gray-600">
                                  <p>
                                    {/* Check if this is from bar supply mode */}
                                    {item.processing?.supplyType === 'barre' ||
                                      (item.productCode && (item.productCode.includes('LAV') || item.productCode.includes('DRAIN'))) ? (
                                      <>
                                        Passo: {item.stepType === 'standard' ? 'Standard' : 'Personalizzato'}
                                        {item.stepType === 'custom' && item.customStepValue && (
                                          <span>
                                            {item.customStepValue === 'progettazione'
                                              ? ' (Progettazione)'
                                              : ` (${item.customStepValue} ${item.drainageType?.includes('fresatura') ? 'fresature' : 'fori'})`
                                            }
                                          </span>
                                        )}
                                      </>
                                    ) : (
                                      <>
                                        Passo: {item.stepType === 'standard' ? 'Standard' : 'Personalizzato'}
                                        {item.stepType === 'custom' && item.customStepValue && (
                                          <span> ({item.customStepValue} mm)</span>
                                        )}
                                      </>
                                    )}
                                  </p>
                                </div>
                              )}

                              {/* Simple display for ricambi items */}
                              {item.isRicambioItem && (
                                <div className="mt-2">
                                  <span className="inline-block text-xs px-3 py-1 rounded-full font-medium bg-orange-100 text-orange-800">
                                    Ricambio
                                  </span>
                                  <p className="text-sm text-gray-600 mt-1">
                                    Quantità: {item.quantity} {articleMetadata[item.productCode]?.unit || item.unit || ''}
                                  </p>
                                </div>
                              )}

                              {item.category && (
                                <p className="text-red-600">
                                  Categoria: {item.category.charAt(0).toUpperCase() + item.category.slice(1)}
                                  {item.subCategory && `${item.subCategory === 'ordina' ? '' : '(Accessori)'}`}
                                </p>
                              )}
                            </div>
                            <div className="flex space-x-2">
                              <div className="flex flex-col space-y-1">

                                {/* View configuration details button */}
                                <div className="relative group">
                                  <button
                                    className="px-2 py-1 bg-indigo-500 text-white rounded hover:bg-indigo-600"
                                    title="Visualizza configurazione"
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                  </button>

                                  <div className="absolute right-0 mt-1 w-80 bg-white rounded-md shadow-lg hidden group-hover:block z-10">
                                    <div className="py-2 px-3">
                                      <div className="text-sm font-medium text-gray-900 border-b pb-2 mb-2">Descrizione Prodotto</div>
                                      <div className="text-sm text-gray-700 whitespace-pre-wrap">
                                        {articleMetadata[item.productCode]?.description || 'Caricamento descrizione...'}
                                      </div>
                                      {articleMetadata[item.productCode]?.unit && (
                                        <div className="text-xs text-gray-500 mt-2 pt-2 border-t">
                                          Unità di misura: {articleMetadata[item.productCode].unit}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* Edit quantity button */}
                                <button
                                  onClick={() => {
                                    const currentQty = item.quantity || item.processing?.quantity || 0;
                                    Swal.fire({
                                      title: 'Modifica Quantità',
                                      html: `<p class="text-sm text-gray-600 mb-2"><strong>${item.productCode}</strong></p>`,
                                      input: 'number',
                                      inputValue: currentQty,
                                      inputAttributes: { min: 1, step: 1 },
                                      showCancelButton: true,
                                      confirmButtonText: 'Aggiorna',
                                      cancelButtonText: 'Annulla',
                                      confirmButtonColor: '#f59e0b',
                                      inputValidator: (value) => {
                                        if (!value || parseFloat(value) <= 0) return 'Inserisci una quantità valida';
                                      }
                                    }).then((result) => {
                                      if (result.isConfirmed) {
                                        const newQty = parseFloat(result.value);
                                        const updatedItem = { ...item };
                                        delete updatedItem.originalIndex;
                                        updatedItem.quantity = newQty;
                                        if (updatedItem.processing) {
                                          updatedItem.processing = { ...updatedItem.processing, quantity: newQty };
                                        }
                                        dispatch({ type: 'UPDATE_CART_ITEM', payload: { index: item.originalIndex, item: updatedItem } });
                                      }
                                    });
                                  }}
                                  className="px-2 py-1 bg-amber-500 text-white rounded hover:bg-amber-600"
                                  title="Modifica quantità"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                  </svg>
                                </button>

                                {/* Move to another variant */}
                                {variantsEnabled && (state.variants || []).length > 1 && (
                                  <button
                                    onClick={() => handleMoveItem(item)}
                                    className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                                    title="Sposta in un'altra variante"
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                              <button
                                onClick={() => {
                                  Swal.fire({
                                    title: 'Conferma',
                                    text: 'Sei sicuro di voler rimuovere questo prodotto dall\'ordine?',
                                    icon: 'warning',
                                    showCancelButton: true,
                                    confirmButtonColor: '#3085d6',
                                    cancelButtonColor: '#d33',
                                    confirmButtonText: 'Sì, rimuovi',
                                    cancelButtonText: 'Annulla'
                                  }).then((result) => {
                                    if (result.isConfirmed) {
                                      // Second confirmation
                                      Swal.fire({
                                        title: 'Sei sicuro sicuro?',
                                        text: 'Il prodotto verrà eliminato definitivamente dal carrello.',
                                        icon: 'question',
                                        showCancelButton: true,
                                        confirmButtonColor: '#dc2626',
                                        cancelButtonColor: '#6b7280',
                                        confirmButtonText: 'Sì, elimina',
                                        cancelButtonText: 'No, tieni'
                                      }).then((secondResult) => {
                                        if (secondResult.isConfirmed) {
                                          dispatch({ type: 'REMOVE_FROM_CART', payload: item.originalIndex });
                                        }
                                      });
                                    }
                                  });
                                }}
                                className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                                title="Rimuovi questo prodotto"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                    </div>
                    {variantsEnabled && (
                      <div className="mt-3 pt-3 border-t border-blue-200">
                        {group.items.length === 0 ? (
                          <p className="text-sm text-blue-700 italic mb-2">Questa variante è vuota: aggiungi gli articoli.</p>
                        ) : (
                          <div className="flex items-baseline justify-between gap-2 mb-2">
                            <span className="text-sm font-semibold text-blue-900">Totale variante</span>
                            <span className="text-right">
                              {group.discountAmount + group.extraDiscountAmount > 0 && (
                                <span className="block text-xs text-gray-500 line-through">€ {formatNumber(group.subtotal)}</span>
                              )}
                              <span className="font-bold text-green-600">€ {formatNumber(group.total)}</span>
                            </span>
                          </div>
                        )}
                        {group.id && (
                          <button
                            onClick={() => handleAddItemsToVariant(group)}
                            disabled={isLoading}
                            className="w-full py-2 text-sm font-semibold text-blue-700 border border-blue-400 rounded-lg hover:bg-blue-100 disabled:opacity-50"
                          >
                            + Aggiungi articoli a questa variante
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 italic">Nessun prodotto nell'ordine</p>
            )}
          </div>

          {/* Pricing Summary */}
          {Array.isArray(state.cartItems) && state.cartItems.length > 0 && (
            <div className="mt-6 border-t pt-6">
              <h3 className="text-lg font-semibold mb-4">Riepilogo Prezzi</h3>

              <div className="space-y-2 mb-4">
                {variantsEnabled ? (
                  <div className="space-y-1 pb-1">
                    {variantTotals.map((g) => (
                      <div key={g.id || 'base'} className="flex justify-between text-sm">
                        <span className="text-gray-600 truncate pr-2">Listino · {g.title}</span>
                        <span className="font-medium whitespace-nowrap">€ {formatNumber(g.subtotal)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotale di listino:</span>
                    <span className="font-medium">€ {formatNumber(calculateSubtotal())}</span>
                  </div>
                )}

                {/* Sconto Tipico (first discount) */}
                <div className="flex justify-between items-center text-sm border-t pt-2">
                  <span className="text-gray-600">Sconto Tipico (%):</span>
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      max="60"
                      step="0.01"
                      value={discountPercentage}
                      disabled
                      className="w-24 px-3 py-2 text-base border rounded text-right bg-gray-100 cursor-not-allowed"
                      placeholder="0"
                    />
                    <span className="font-medium">%</span>
                    <button
                      onClick={() => setShowDiscountModal('tipico')}
                      className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                      title="Modifica Sconto Tipico"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                  </div>
                </div>

                {!variantsEnabled && discountPercentage > 0 && (
                  <div className="flex justify-between text-sm text-orange-600">
                    <span>Importo Sconto Tipico:</span>
                    <span className="font-medium">- € {formatNumber(calculateDiscountAmount())}</span>
                  </div>
                )}

                {/* Subtotal after first discount */}
                {!variantsEnabled && discountPercentage > 0 && (
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>Subtotale dopo Sconto Tipico:</span>
                    <span className="font-medium">€ {formatNumber(calculateSubtotalAfterFirstDiscount())}</span>
                  </div>
                )}

                {/* Sconto Extra (second discount) */}
                <div className="flex justify-between items-center text-sm border-t pt-2">
                  <span className="text-gray-600">Sconto Extra (%):</span>
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      max="15"
                      step="0.01"
                      value={extraDiscountPercentage}
                      disabled
                      className="w-24 px-3 py-2 text-base border rounded text-right bg-gray-100 cursor-not-allowed"
                      placeholder="0"
                    />
                    <span className="font-medium">%</span>
                    <button
                      onClick={() => setShowDiscountModal('extra')}
                      className="px-2 py-1 text-xs bg-purple-500 text-white rounded hover:bg-purple-600"
                      title="Modifica Sconto Extra"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                  </div>
                </div>

                {!variantsEnabled && extraDiscountPercentage > 0 && (
                  <div className="flex justify-between text-sm text-purple-600">
                    <span>Importo Sconto Extra:</span>
                    <span className="font-medium">- € {formatNumber(calculateExtraDiscountAmount())}</span>
                  </div>
                )}

                {variantsEnabled ? (
                  <div className="border-t pt-2 mt-2 space-y-1">
                    {variantTotals.map((g) => (
                      <div key={g.id || 'base'} className="flex justify-between text-sm font-semibold">
                        <span className="text-gray-800 truncate pr-2">Totale · {g.title}</span>
                        <span className="text-green-600 whitespace-nowrap">€ {formatNumber(g.total)}</span>
                      </div>
                    ))}
                    {variantTotals.length > 1 && (
                      <div className="flex justify-between text-lg font-bold border-t pt-2 mt-2">
                        <span>Totale medio:</span>
                        <span className="text-green-600">€ {formatNumber(averageOf('total'))}</span>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 italic pt-1">Gli sconti si applicano a ogni variante. Le varianti sono alternative tra loro: non vanno sommate.</p>
                  </div>
                ) : (
                  <div className="flex justify-between text-lg font-bold border-t pt-2 mt-2">
                    <span>Totale:</span>
                    <span className="text-green-600">€ {formatNumber(calculateTotal())}</span>
                  </div>
                )}
                <p className="text-xs text-gray-500 italic mt-2">Prezzi esclusi di iva e servizi</p>
              </div>
            </div>
          )}

          {/* Discount Edit Modal */}
          {showDiscountModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
                <h3 className="text-lg font-semibold mb-4">
                  {showDiscountModal === 'tipico' ? 'Modifica Sconto Tipico' : 'Modifica Sconto Extra'}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  {showDiscountModal === 'tipico'
                    ? 'Inserisci il nuovo valore per lo Sconto Tipico (applicato sul subtotale)'
                    : 'Inserisci il nuovo valore per lo Sconto Extra (applicato dopo lo Sconto Tipico)'}
                </p>
                {/* Show minimum discount warning for Sconto Tipico */}
                {showDiscountModal === 'tipico' && state.customer?.DiscountFormula && (
                  <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                    <strong>Nota:</strong> Lo sconto minimo per questo cliente è {parseFloat(state.customer.DiscountFormula) || 0}%
                  </div>
                )}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Percentuale Sconto
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      inputMode="decimal"
                      min={showDiscountModal === 'tipico' ? (parseFloat(state.customer?.DiscountFormula) || 0) : 0}
                      max={showDiscountModal === 'tipico' ? "60" : "15"}
                      step="0.01"
                      id="discount-modal-input"
                      defaultValue={showDiscountModal === 'tipico' ? discountPercentage : extraDiscountPercentage}
                      className="w-full px-3 py-3 text-base border rounded-lg focus:ring-2 focus:ring-red-500"
                      placeholder="0"
                      autoFocus
                    />
                    <span className="font-medium">%</span>
                  </div>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowDiscountModal(null)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Annulla
                  </button>
                  <button
                    onClick={() => {
                      const input = document.getElementById('discount-modal-input');
                      const value = parseFloat(input.value) || 0;

                      // For Sconto Tipico, enforce minimum based on customer's DiscountFormula and maximum of 60%
                      if (showDiscountModal === 'tipico') {
                        const minDiscount = parseFloat(state.customer?.DiscountFormula) || 0;
                        const maxDiscount = 60;
                        if (value < minDiscount) {
                          Swal.fire({
                            title: 'Errore',
                            text: `Lo Sconto Tipico non può essere inferiore a ${minDiscount}% (sconto associato al cliente)`,
                            icon: 'error'
                          });
                          return;
                        }
                        if (value > maxDiscount) {
                          Swal.fire({
                            title: 'Errore',
                            text: `Lo Sconto Tipico non può essere superiore a ${maxDiscount}%`,
                            icon: 'error'
                          });
                          return;
                        }
                        const clampedValue = Math.max(minDiscount, Math.min(maxDiscount, value));
                        setDiscountPercentage(clampedValue);
                      } else {
                        // For Sconto Extra, enforce maximum of 15%
                        const maxDiscount = 15;
                        if (value > maxDiscount) {
                          Swal.fire({
                            title: 'Errore',
                            text: `Lo Sconto Extra non può essere superiore a ${maxDiscount}%`,
                            icon: 'error'
                          });
                          return;
                        }
                        const clampedValue = Math.max(0, Math.min(maxDiscount, value));
                        setExtraDiscountPercentage(clampedValue);
                      }
                      setShowDiscountModal(null);
                    }}
                    className={`px-4 py-2 text-white rounded-lg ${showDiscountModal === 'tipico' ? 'bg-red-500 hover:bg-red-600' : 'bg-purple-500 hover:bg-purple-600'
                      }`}
                  >
                    Conferma
                  </button>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="mt-4 p-3 bg-red-100 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          {variantsEnabled && (state.variants || []).length > 1 && (
            <div className="mt-6 flex flex-wrap items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
              <span className="text-blue-900 font-medium">Nuovi articoli in:</span>
              <select
                value={state.activeVariantId || ''}
                onChange={(e) => dispatch({ type: 'SET_ACTIVE_VARIANT', payload: e.target.value })}
                className="flex-1 min-w-[10rem] px-2 py-1.5 border border-blue-300 rounded-md bg-white text-blue-900 focus:ring-2 focus:ring-blue-400"
              >
                {state.variants.map((v) => <option key={v.id} value={v.id}>{v.title}</option>)}
              </select>
            </div>
          )}

          <div className="mt-8 grid grid-cols-2 gap-3">
            <button
              onClick={handleNewProduct}
              disabled={isLoading}
              className="py-2 px-4 border border-red-500 text-red-500 rounded-lg hover:bg-red-50 disabled:opacity-50"
            >
              AGGIUNGI ALTRI ARTICOLI
            </button>
            <button
              onClick={handleSaveAsDraft}
              disabled={isLoading || (Array.isArray(state.cartItems) && state.cartItems.length === 0)}
              className="py-2 px-4 border border-yellow-500 text-yellow-700 rounded-lg hover:bg-yellow-50 disabled:opacity-50"
            >
              {orderType === 'offerta' ? 'SALVA OFFERTA PER DOPO' : 'SALVA ORDINE PER DOPO'}
            </button>
            {orderType === 'offerta' && (
              <button
                onClick={handleAddVariant}
                disabled={isLoading}
                className="py-2 px-4 border border-blue-500 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 col-span-2 font-medium"
              >
                + AGGIUNGI VARIANTE
              </button>
            )}
            <button
              onClick={handleCompleteOrder}
              disabled={isLoading || (Array.isArray(state.cartItems) && state.cartItems.length === 0)}
              className="py-2 px-4 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 col-span-2"
            >
              {isLoading ? 'Elaborazione...' : (orderType === 'offerta' ? 'COMPLETA OFFERTA' : 'COMPLETA ORDINE')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;
